import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { useContent } from '../../lib/content';
import { pickLocale } from '../../lib/api';

export default function ComparisonTable() {
  const { content, locale } = useContent();
  const data = content.comparison || {};
  const rows = data.rows || [];
  const left = data.left_column || {};
  const right = data.right_column || {};

  return (
    <section
      id="comparison"
      data-testid="comparison-section"
      className="section bg-bg overflow-hidden relative"
    >
      <div className="container-x">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-20">
          <div className="md:col-span-7">
            <p className="text-eyebrow mb-4">{pickLocale(data.eyebrow, locale)}</p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-ink" data-testid="comparison-title">
              {pickLocale(data.title, locale)}
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col justify-end">
            <p className="text-ink-muted text-base md:text-lg leading-relaxed">
              {pickLocale(data.subtitle, locale)}
            </p>
          </div>
        </div>

        {/* Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
          className="border border-line rounded-md overflow-hidden bg-bg-secondary"
          data-testid="comparison-table"
        >
          {/* Column headers */}
          <div className="grid grid-cols-[1.4fr_1fr_1fr] md:grid-cols-[2fr_1fr_1fr]">
            <div className="bg-bg p-5 md:p-6 border-b border-r border-line"></div>
            <div className="bg-bg p-4 md:p-6 border-b border-r border-line text-center" data-testid="comparison-left-header">
              <div className="text-eyebrow text-ink-muted mb-1.5">{pickLocale(left.label, locale)}</div>
              <div className="text-[10px] md:text-[11px] font-mono text-ink-dim leading-snug">{pickLocale(left.subtitle, locale)}</div>
            </div>
            <div className="bg-bg p-4 md:p-6 border-b border-line text-center relative" data-testid="comparison-right-header">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-acid" />
              <div className="text-eyebrow text-acid mb-1.5">{right.label || 'MODARIA'}</div>
              <div className="text-[10px] md:text-[11px] font-mono text-ink-dim leading-snug">{pickLocale(right.subtitle, locale)}</div>
            </div>
          </div>

          {/* Rows */}
          {rows.map((r, i) => (
            <div
              key={i}
              className={`grid grid-cols-[1.4fr_1fr_1fr] md:grid-cols-[2fr_1fr_1fr] ${i % 2 === 0 ? 'bg-bg-secondary' : 'bg-bg-secondary/40'}`}
              data-testid={`comparison-row-${i}`}
            >
              <div className="p-4 md:p-5 border-r border-line text-sm md:text-[15px] text-ink leading-snug">
                {pickLocale(r.label, locale)}
              </div>
              <div className="p-4 md:p-5 border-r border-line flex items-center justify-center">
                {r.left ? (
                  <Check size={20} className="text-ink-muted" />
                ) : (
                  <X size={20} className="text-ink-dim/60" />
                )}
              </div>
              <div className="p-4 md:p-5 flex items-center justify-center">
                {r.right ? (
                  <Check size={20} className="text-acid" strokeWidth={2.5} />
                ) : (
                  <X size={20} className="text-ink-dim/60" />
                )}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Footer note */}
        {data.footer_note && (
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-10 md:mt-14 font-display italic text-2xl md:text-4xl text-ink-muted text-center max-w-3xl mx-auto leading-tight"
            data-testid="comparison-footer-note"
          >
            "{pickLocale(data.footer_note, locale)}"
          </motion.p>
        )}
      </div>
    </section>
  );
}
